import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Rocket, FileSignature, GraduationCap, ScrollText, ExternalLink,
  Lock, CheckCircle2, AlarmClock, Sparkles, CalendarDays, Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  {
    id: 'phase1',
    icon: FileSignature,
    number: '01',
    title: 'Contracting & Resources',
    accent: 'from-[#4318FF] to-[#2447D6]',
    note: "Two portals for contracting. One is for Breeze, the other is with our annuity partners at Aegis Financial. Each portal needs its own username and password — an account on one does not carry over to the other. Use the New Agent Guide to know which carriers specifically to request.",
    items: [
      { id: 'guide_save', label: 'New Agent Guide (Save)', url: 'https://online.fliphtml5.com/pqlyu/jqxk/#p=1', linkText: 'Open the New Agent Guide' },
      { id: 'surelc_breeze', label: 'First SureLC Link (Breeze)', emphasis: 'Complete Contracting & Bookmark this Link', url: 'https://accounts.surancebay.com/oauth/authorize?redirect_uri=https:%2F%2Fsurelc.surancebay.com%2Fproducer%2Foauth%3FreturnUrl%3D%252Fprofile%252Fcontact-info%253FgaId%253D1139%2526gaId%253D1139%2526branchVisible%253Dtrue%2526branchEditable%253Dfalse%2526branchRequired%253Dtrue%2526dba%253DP%2526autoAdd%253Dfalse%2526requestMethod%253DGET&gaId=1139&client_id=surecrmweb&response_type=code', linkText: 'Open SureLC — Breeze' },
      { id: 'surelc_aegis', label: 'Second SureLC Link (Aegis)', emphasis: 'Complete Contracting & Bookmark this Link', url: 'https://accounts.surancebay.com/oauth/authorize?redirect_uri=https:%2F%2Fsurelc.surancebay.com%2Fproducer%2Foauth%3FreturnUrl%3D%252Fprofile%252Fcontact-info%253FgaId%253D145%2526gaId%253D145%2526branch%253DAegis%252520-%252520Breeze%252520Wealth%252520Management%2526branchVisible%253Dtrue%2526branchEditable%253Dfalse%2526branchRequired%253Dtrue%2526autoAdd%253Dfalse%2526requestMethod%253DGET&gaId=145&client_id=surecrmweb&response_type=code', linkText: 'Open SureLC — Aegis' },
      { id: 'eo_insurance', label: 'Errors & Omissions Insurance (E&O)', url: 'https://www.nextinsurance.com', linkText: 'Nextinsurance.com' },
      { id: 'aml', label: 'AML Training', url: 'https://aml.surancebay.com', linkText: 'aml.surancebay.com' },
    ],
  },
  {
    id: 'phase2',
    icon: GraduationCap,
    number: '02',
    title: 'Training & Development',
    accent: 'from-[#01B574] to-[#43E97B]',
    note: null,
    items: [
      { id: 'blitz_training', label: 'Complete Blitz Training Matrix — within 72 hours', description: 'Training platform to understand the fundamentals behind what we do and what we sell.', url: 'https://start.blitztrainingmatrix.com', linkText: 'start.blitztrainingmatrix.com', urgent: true },
      { id: 'drive_access', label: 'Request access to the Google Drive', description: 'Holds our recorded trainings around sales, IULs, annuities, and top producer interviews.', url: 'https://drive.google.com/drive/folders/1hQt8n-yIcpCSwpLzbRFzszNoX7e-EHuJ', linkText: 'Request Drive Access' },
      { id: 'iul_script', label: 'Breeze IUL Script (save this link somewhere)', url: 'https://app.notion.com/p/Breeze-IUL-Script-247423bc43ce8013b4facd3fd4f57f52', linkText: 'Open IUL Script' },
      { id: 'life_script', label: 'Breeze General Life Script (save this link somewhere)', url: 'https://app.notion.com/p/Breeze-General-Life-Script-38d423bc43ce80a4a911e239963807e2', linkText: 'Open General Life Script' },
      { id: 'one_on_ones', label: 'Schedule your first two 1-on-1 trainings with your upline', description: 'This is to be proactive — reach out and get them on the calendar. Have the script practiced by then. Check the box once booked on your calendar(s).' },
    ],
  },
];

const ACK_TEXT = "I understand that speed matters, and I commit to taking action quickly instead of waiting until I feel completely ready. I will listen to, trust, and follow the guidance of my uplines, recognizing that their experience can help me avoid unnecessary mistakes. I will remain coachable, open to feedback, and willing to adjust when something I am doing is not producing the desired result. I will maintain a positive, solution-oriented attitude even when I face rejection, setbacks, or uncomfortable parts of the learning process. Most importantly, I understand that I am personally responsible for my effort, consistency, decisions, and ultimately the outcomes I create in this business.";

const TOTAL_ITEMS = PHASES.reduce((n, p) => n + p.items.length, 0) + 1;

export default function Onboarding() {
  const { user, getAuthHeader } = useAuth();
  const [checked, setChecked] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/onboarding`, getAuthHeader())
      .then((res) => { setChecked(res.data.checked_items || []); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once
  }, []);

  const toggle = async (itemId) => {
    if (saving) return;
    const isChecked = !checked.includes(itemId);
    setSaving(true);
    try {
      const res = await axios.post(`${API}/onboarding/check`, { item_id: itemId, checked: isChecked }, getAuthHeader());
      setChecked(res.data.checked_items);
      if (res.data.complete) {
        toast.success('Checklist complete — welcome to ATLAS! Unlocking full access...');
        setTimeout(() => { window.location.href = '/'; }, 1600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const done = checked.length;
  const pct = Math.round((done / TOTAL_ITEMS) * 100);
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="max-w-[920px] mx-auto space-y-8 pb-10" data-testid="onboarding-page">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#0B1437] via-[#111C44] to-[#1B254B] p-8 shadow-[0px_24px_50px_rgba(11,20,55,0.35)]">
        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#4318FF]/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 left-16 h-52 w-52 rounded-full bg-[#01B574]/30 blur-3xl" />
        <div className="pointer-events-none absolute right-32 bottom-0 h-32 w-32 rounded-full bg-[#F6C561]/25 blur-2xl" />
        <div className="relative flex flex-wrap items-center gap-5">
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4318FF] to-[#7000FF] shadow-[0_12px_30px_rgba(67,24,255,0.5)]"
          >
            <Rocket className="h-8 w-8 text-white" strokeWidth={2} />
          </motion.div>
          <div className="flex-1 min-w-[240px]">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#F6C561]">
              <Sparkles className="h-3.5 w-3.5" /> New Agent Launch Pad
            </p>
            <h1 className="mt-1 font-['Manrope'] text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Welcome to Breeze{firstName ? `, ${firstName}` : ''}.
            </h1>
            <p className="mt-1.5 text-sm font-medium text-white/60">
              Complete every step below to unlock full access to ATLAS. Your progress saves automatically.
            </p>
          </div>
        </div>
        <div className="relative mt-6 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
          <div className="flex items-center justify-between text-[12px] font-bold">
            <span className="flex items-center gap-1.5 text-white/70">
              <Lock className="h-3.5 w-3.5 text-[#F6C561]" /> {done} of {TOTAL_ITEMS} steps complete
            </span>
            <span className="font-['Manrope'] text-lg font-extrabold text-white" data-testid="onboarding-progress-pct">{pct}%</span>
          </div>
          <div className="mt-2.5 h-3 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#01B574] via-[#43E97B] to-[#F6C561]"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>
      </motion.div>

      {/* Phases */}
      {PHASES.map((phase, pi) => {
        const PhaseIcon = phase.icon;
        const phaseDone = phase.items.filter((i) => checked.includes(i.id)).length;
        return (
          <motion.section key={phase.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + pi * 0.08 }}>
            <div className="mb-4 flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${phase.accent} shadow-lg`}>
                <PhaseIcon className="h-5.5 w-5.5 text-white" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#A3AED0]">Phase {phase.number}</p>
                <h2 className="font-['Manrope'] text-xl font-extrabold text-[#2B3674]">{phase.title}</h2>
              </div>
              <span className="rounded-full bg-[#E9EDF7] px-3 py-1 text-[11px] font-bold text-[#2B3674]/70">
                {phaseDone}/{phase.items.length}
              </span>
            </div>
            {phase.note && (
              <div className="mb-4 rounded-2xl border-l-4 border-[#4318FF] bg-gradient-to-r from-[#4318FF]/5 to-transparent px-5 py-4 text-[13px] leading-relaxed text-[#2B3674]/85">
                <span className="font-extrabold text-[#4318FF]">Heads up:</span> {phase.note}
              </div>
            )}
            <div className="space-y-3">
              {phase.items.map((item) => {
                const isDone = checked.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className={`group flex cursor-pointer items-start gap-4 rounded-2xl border-2 px-5 py-4 transition-all duration-200 ${
                      isDone
                        ? 'border-[#01B574]/40 bg-gradient-to-r from-[#F0FBF6] to-white shadow-[0_4px_14px_rgba(1,181,116,0.12)]'
                        : 'border-[#E9EDF7] bg-white shadow-[0_4px_14px_rgba(112,144,176,0.08)] hover:-translate-y-0.5 hover:border-[#4318FF]/35 hover:shadow-[0_10px_24px_rgba(67,24,255,0.12)]'
                    }`}
                    data-testid={`onboarding-item-${item.id}`}
                  >
                    <Checkbox
                      checked={isDone}
                      onCheckedChange={() => toggle(item.id)}
                      className="mt-0.5 h-5 w-5"
                      data-testid={`onboarding-check-${item.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className={`text-[15px] font-bold ${isDone ? 'text-[#01B574]' : 'text-[#2B3674]'}`}>
                        {item.label}
                        {item.urgent && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-bold text-amber-700">
                            <AlarmClock className="h-3 w-3" /> 72 HOURS
                          </span>
                        )}
                      </p>
                      {item.emphasis && (
                        <p className="mt-0.5 text-[12px] font-extrabold italic tracking-wide text-[#B47E14]">
                          ★ {item.emphasis}
                        </p>
                      )}
                      {item.description && <p className="mt-0.5 text-xs leading-relaxed text-[#707EAE]">{item.description}</p>}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-[#4318FF]/8 px-2.5 py-1 text-xs font-bold text-[#1E3FBF] transition-colors hover:bg-[#4318FF]/15"
                        >
                          <ExternalLink className="h-3 w-3" /> {item.linkText || item.url}
                        </a>
                      )}
                    </div>
                    {isDone && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[#01B574]" />
                      </motion.div>
                    )}
                  </label>
                );
              })}

              {/* Agency trainings — key note (not a checkbox) */}
              {phase.id === 'phase2' && (
                <div
                  className="flex items-start gap-4 rounded-2xl border-2 border-dashed border-[#F6C561]/60 bg-gradient-to-r from-[#FFF9EC] to-white px-5 py-4"
                  data-testid="onboarding-note-agency-trainings"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#F6C561] to-[#D89E2F] shadow-md">
                    <CalendarDays className="h-4.5 w-4.5 text-white" />
                  </div>
                  <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#B47E14]">Key Note — Breeze Agency Trainings</p>
                    <p className="mt-0.5 text-sm font-semibold text-[#2B3674]">
                      Monday, Tuesday, Thursday & Friday @ 2PM CST
                    </p>
                    <p className="text-xs text-[#707EAE]">Zoom links are dropped in the Agency Telegram chat day of. Show up — this is where momentum is built.</p>
                  </div>
                </div>
              )}
            </div>
          </motion.section>
        );
      })}

      {/* Acknowledgement */}
      <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F6C561] to-[#D89E2F] shadow-lg">
            <ScrollText className="h-5.5 w-5.5 text-white" strokeWidth={2} />
          </div>
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#A3AED0]">Final Step</p>
            <h2 className="font-['Manrope'] text-xl font-extrabold text-[#2B3674]">Acknowledgement & Agreement</h2>
          </div>
        </div>
        <div className={`relative overflow-hidden rounded-[20px] border-2 p-6 transition-colors ${
          checked.includes('acknowledgement')
            ? 'border-[#01B574]/50 bg-gradient-to-br from-[#F0FBF6] to-white'
            : 'border-[#F6C561]/60 bg-gradient-to-br from-[#0B1437] to-[#1B254B]'
        }`}>
          {!checked.includes('acknowledgement') && (
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#F6C561]/20 blur-3xl" />
          )}
          <div className="relative">
            <p className={`flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.2em] ${checked.includes('acknowledgement') ? 'text-[#01B574]' : 'text-[#F6C561]'}`}>
              <Flame className="h-3.5 w-3.5" /> The Breeze Standard
            </p>
            <p className={`mt-3 text-[13.5px] leading-relaxed ${checked.includes('acknowledgement') ? 'text-[#2B3674]/90' : 'text-white/85'}`}>
              {ACK_TEXT}
            </p>
            <label className="mt-5 flex cursor-pointer items-center gap-3" data-testid="onboarding-item-acknowledgement">
              <Checkbox
                checked={checked.includes('acknowledgement')}
                onCheckedChange={() => toggle('acknowledgement')}
                className="h-5 w-5"
                data-testid="onboarding-check-acknowledgement"
              />
              <span className={`text-sm font-extrabold ${checked.includes('acknowledgement') ? 'text-[#01B574]' : 'text-white'}`}>
                I have read, understood, and agree to the above.
              </span>
            </label>
          </div>
        </div>
      </motion.section>

      <p className="text-center text-xs font-medium text-[#A3AED0]">
        Full ATLAS access unlocks automatically the moment every box is checked. Let's get to work. 🔥
      </p>
    </div>
  );
}
